import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SessionTimer = ({ 
  isActive = true, 
  isPaused = false,
  onTimeUpdate,
  initialTime = 0 
}) => {
  const [elapsedTime, setElapsedTime] = useState(initialTime);
  const [restTime, setRestTime] = useState(0);
  const [isResting, setIsResting] = useState(false);

  useEffect(() => {
    let interval = null;
    
    if (isActive && !isPaused) {
      interval = setInterval(() => {
        setElapsedTime(time => {
          const newTime = time + 1;
          if (onTimeUpdate) {
            onTimeUpdate(newTime);
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isActive, isPaused, onTimeUpdate]);

  useEffect(() => {
    let restInterval = null;
    
    if (isResting && !isPaused) {
      restInterval = setInterval(() => {
        setRestTime(time => time + 1);
      }, 1000);
    } else if (!isResting) {
      setRestTime(0);
    }

    return () => {
      if (restInterval) {
        clearInterval(restInterval);
      }
    };
  }, [isResting, isPaused]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
    }
    return `${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const startRest = () => {
    setIsResting(true);
    setRestTime(0);
  };

  const endRest = () => {
    setIsResting(false);
    setRestTime(0);
  };

  return (
    <div className="bg-surface rounded-2xl p-6 shadow-lg border border-border">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-foreground mb-4">
          Session Timer
        </h3>

        {/* Main Session Time */}
        <div className="mb-6">
          <div className="flex items-center justify-center space-x-3 mb-2">
            <Icon 
              name={isPaused ? 'Pause' : 'Clock'} 
              size={24} 
              className={isPaused ? 'text-warning' : 'text-primary'} 
            />
            <span className="text-3xl font-mono font-bold text-foreground">
              {formatTime(elapsedTime)}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Total Session Time
          </p>
        </div>

        {/* Rest Timer */}
        {isResting && (
          <div className="mb-6 p-4 bg-accent rounded-xl">
            <div className="flex items-center justify-center space-x-3 mb-2">
              <Icon name="Coffee" size={20} className="text-secondary" />
              <span className="text-2xl font-mono font-bold text-secondary">
                {formatTime(restTime)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Rest Time
            </p>
          </div>
        )}

        {/* Status Indicators */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className={`w-4 h-4 rounded-full mx-auto mb-2 ${
              isActive && !isPaused ? 'bg-success animate-gentle-pulse' : 'bg-muted'
            }`} />
            <p className="text-xs text-muted-foreground">
              {isActive && !isPaused ? 'Active' : 'Paused'}
            </p>
          </div>
          
          <div className="text-center">
            <div className={`w-4 h-4 rounded-full mx-auto mb-2 ${
              isResting ? 'bg-secondary animate-gentle-pulse' : 'bg-muted'
            }`} />
            <p className="text-xs text-muted-foreground">
              {isResting ? 'Resting' : 'Training'}
            </p>
          </div>
        </div>

        {/* Rest Controls */}
        <div className="mt-6 pt-4 border-t border-border">
          {!isResting ? (
            <button
              onClick={startRest}
              className="text-sm text-secondary hover:text-secondary/80 transition-colors duration-200"
            >
              Start Rest Period
            </button>
          ) : (
            <button
              onClick={endRest}
              className="text-sm text-primary hover:text-primary/80 transition-colors duration-200"
            >
              End Rest Period
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionTimer;